import React, { useState } from 'react';
import { CogIcon, FileTextIcon } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
interface Application {
  id: string;
  downPaymentVerification: {
    threshold: number;
    period: {
      start: string;
      end: string;
      days: number;
    };
  };
  [key: string]: any;
}
interface DownPaymentSectionProps {
  application: Application;
}
export function DownPaymentSection({
  application
}: DownPaymentSectionProps) {
  const {
    addToast
  } = useToast();
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [config, setConfig] = useState({
    threshold: application.downPaymentVerification.threshold,
    startDate: application.downPaymentVerification.period.start,
    endDate: application.downPaymentVerification.period.end
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setConfig({
      ...config,
      [name]: value
    });
  };
  const handleSaveConfig = () => {
    // In a real app, this would make an API call to update the configuration
    console.log('Saving configuration:', config);
    setIsConfiguring(false);
    addToast('Configuration saved successfully', 'success');
  };
  const handleGenerateReport = () => {
    // In a real app, this would generate a report
    addToast('Generating verification report...', 'info');
  };
  return <div className="bg-white rounded-md shadow-sm border border-neutral-200 overflow-hidden" data-id="element-274">
      <div className="flex items-center justify-between p-4 border-b border-neutral-200" data-id="element-275">
        <h2 className="text-xl font-medium text-neutral-900" data-id="element-276">
          Down Payment Verification
        </h2>
        <button onClick={() => setIsConfiguring(!isConfiguring)} className="inline-flex items-center px-3 py-1 border border-neutral-300 text-sm font-medium rounded-md bg-white text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-277">
          <CogIcon className="mr-1 h-4 w-4" data-id="element-278" />
          Configure
        </button>
      </div>
      {isConfiguring ? <div className="p-6" data-id="element-279">
          <h3 className="text-lg font-medium text-neutral-900 mb-4" data-id="element-280">
            Verification Settings
          </h3>
          <div className="space-y-4 max-w-md" data-id="element-281">
            <div data-id="element-282">
              <label htmlFor="threshold" className="block text-sm font-medium text-neutral-700 mb-1" data-id="element-283">
                Large Deposit Threshold ($)
              </label>
              <input type="number" id="threshold" name="threshold" value={config.threshold} onChange={handleInputChange} className="input w-full" min="0" step="100" data-id="element-284" />
              <p className="mt-1 text-xs text-neutral-500" data-id="element-285">
                Deposits above this amount will be flagged for verification
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-286">
              <div data-id="element-287">
                <label htmlFor="startDate" className="block text-sm font-medium text-neutral-700 mb-1" data-id="element-288">
                  Start Date
                </label>
                <input type="date" id="startDate" name="startDate" value={config.startDate} onChange={handleInputChange} className="input w-full" data-id="element-289" />
              </div>
              <div data-id="element-290">
                <label htmlFor="endDate" className="block text-sm font-medium text-neutral-700 mb-1" data-id="element-291">
                  End Date
                </label>
                <input type="date" id="endDate" name="endDate" value={config.endDate} onChange={handleInputChange} className="input w-full" data-id="element-292" />
              </div>
            </div>
            <div className="pt-4" data-id="element-293">
              <button onClick={handleSaveConfig} className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium" data-id="element-294">
                Save Configuration
              </button>
            </div>
          </div>
        </div> : <div className="p-6" data-id="element-295">
          <div className="mb-6" data-id="element-296">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-297">
              <div className="bg-neutral-50 rounded-md p-4" data-id="element-298">
                <h3 className="text-sm font-medium text-neutral-500 mb-1" data-id="element-299">
                  Large Deposit Threshold
                </h3>
                <p className="text-xl font-semibold text-neutral-900" data-id="element-300">
                  $
                  {application.downPaymentVerification.threshold.toLocaleString()}
                </p>
              </div>
              <div className="bg-neutral-50 rounded-md p-4" data-id="element-301">
                <h3 className="text-sm font-medium text-neutral-500 mb-1" data-id="element-302">
                  Verification Period
                </h3>
                <p className="text-base text-neutral-900" data-id="element-303">
                  {new Date(application.downPaymentVerification.period.start).toLocaleDateString()}{' '}
                  to{' '}
                  {new Date(application.downPaymentVerification.period.end).toLocaleDateString()}
                  <span className="text-neutral-500 text-sm ml-1" data-id="element-304">
                    ({application.downPaymentVerification.period.days} days,
                    inclusive)
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-305">
            <h3 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-306">
              Verification Status
            </h3>
            <div className="bg-warning-50 border border-warning-200 rounded-md p-4 mb-4" data-id="element-307">
              <div className="flex" data-id="element-308">
                <div className="flex-shrink-0" data-id="element-309">
                  <svg className="h-5 w-5 text-warning-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-310">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" data-id="element-311" />
                  </svg>
                </div>
                <div className="ml-3" data-id="element-312">
                  <h3 className="text-sm font-medium text-warning-800" data-id="element-313">
                    Verification Pending
                  </h3>
                  <div className="mt-2 text-sm text-warning-700" data-id="element-314">
                    <p data-id="element-315">
                      Down payment verification has not been completed. Upload
                      bank statements and generate a verification report.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={handleGenerateReport} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-316">
              <FileTextIcon className="mr-2 h-4 w-4" data-id="element-317" />
              Generate Verification Report
            </button>
          </div>
          <div data-id="element-318">
            <h3 className="text-lg font-medium text-neutral-900 mb-3" data-id="element-319">
              Required Documents
            </h3>
            <ul className="space-y-2" data-id="element-320">
              <li className="flex items-start" data-id="element-321">
                <div className="flex-shrink-0 h-5 w-5 text-success-500" data-id="element-322">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-323">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-324" />
                  </svg>
                </div>
                <span className="ml-2 text-neutral-700" data-id="element-325">
                  Bank statements covering the verification period
                </span>
              </li>
              <li className="flex items-start" data-id="element-326">
                <div className="flex-shrink-0 h-5 w-5 text-neutral-400" data-id="element-327">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-328">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" data-id="element-329" />
                  </svg>
                </div>
                <span className="ml-2 text-neutral-700" data-id="element-330">
                  Gift letter (if applicable)
                </span>
              </li>
              <li className="flex items-start" data-id="element-331">
                <div className="flex-shrink-0 h-5 w-5 text-neutral-400" data-id="element-332">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-333">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" data-id="element-334" />
                  </svg>
                </div>
                <span className="ml-2 text-neutral-700" data-id="element-335">
                  Proof of sale of assets (if applicable)
                </span>
              </li>
            </ul>
          </div>
        </div>}
    </div>;
}